import {
  useCreateBettingSession,
  useCreateBettingSessionDemo,
} from '@/api/betting-sessions';
import { useGameBySlug } from '@/api/games';
import { useWalletsMe } from '@/api/wallets';
import { CookiesKey } from '@/enums/cookiesKey';
import { ModalsKey } from '@/enums/modalsKey';
import { RootState, store } from '@/lib/store';
import {
  setCasinoPlayCoin,
  setHasSlotSession,
} from '@/lib/store/casino/actions';
import { CasinoGameMode } from '@/lib/store/casino/props';
import { GamingAggregatorId } from '@/models/gaming-aggregator/id';
import { GenericError } from '@/models/generic-error';
import { Language } from '@/models/language';
import { AuthService } from '@/services/AuthService';
import { CreateBettingSessionDemoInputDto } from '@/services/dto/betiing-session/create-betting-session-demo-input.dto';
import { CreateBettingSessionInputDto } from '@/services/dto/betiing-session/create-betting-session-input.dto';
import { useIsMobile, useToggle } from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import { Coin, CoinType } from '@starsoft/common/models';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import { AxiosError } from 'axios';
import { getCookie, setCookie } from 'cookies-next';
import { debounce } from 'lodash';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModalNavigation } from '../modals/useModalNavigation';

export function useSlotGame(enabled = true) {
  const dispatch = useDispatch();
  const { lang: defaultLanguage } = useTranslation();
  const cookieLang = getCookie(CookiesKey.CasinoLang) as string;
  const [lang, setLang] = useState<string>(cookieLang);
  const playCoin: Nullable<Coin> = useSelector(
    (state: RootState) => state.casino.playCoin,
  );

  const {
    wallet,
    isLoading: isLoadingWallet,
    error: errorWallet,
    refetch: refetchWallet,
  } = useWalletsMe();

  const isMobile = useIsMobile();
  const [showModeSelection, toggleModeSelection] = useToggle(!isMobile);
  const { navigateToModal } = useModalNavigation();
  const currentMode: CasinoGameMode = useSelector(
    (state: RootState) => state.casino.mode,
  );

  const {
    query: { gameSlug, mode },
    //@ts-ignore
    pathname,
  } = useRouter();
  const [currentPathname, setCurrentPathname] = useState(pathname);

  const selectedMode = mode ?? currentMode;
  const hasSlotSession: boolean = useSelector(
    (state: RootState) => state.casino.hasSlotSession,
  );

  const {
    game,
    error: errorGame,
    refetch: refetchGame,
    isLoading: isLoadingGame,
    gameStatus,
  } = useGameBySlug({ slug: gameSlug as string });

  const {
    createBettingSessionDemo,
    isLoading: isLoadingDemoSlotSession,
    error: errorDemoSlotSession,
    isError: isErrorDemoSlotSession,
    reset: resetDemoSlotSession,
    data: dataDemoSlotSession,
  } = useCreateBettingSessionDemo();

  const {
    createBettingSession,
    isLoading: isLoadingSlotSession,
    isError: isErrorSlotSession,
    error: errorSlotSession,
    reset: resetSlotSession,
    data: dataSlotSession,
  } = useCreateBettingSession();

  const getIsAvailableLang = useCallback(() => {
    return !!game?.provider?.languages?.find(data =>
      data?.language?.code.includes(lang?.slice(0, 2)),
    );
  }, [game?.provider?.languages, lang]);

  const isAvailableLang: boolean = useMemo(getIsAvailableLang, [
    game,
    lang,
    getIsAvailableLang,
  ]);

  const getIsAvailableCoin = useCallback(() => {
    if (wallet?.currentAccount?.coin?.type == CoinType.Crypto) {
      return true;
    }
    return !!game?.provider?.coins?.find(
      data => data?.coin?.id === wallet?.currentAccount?.coin?.id,
    );
  }, [game?.provider?.coins, wallet?.currentAccount]);

  const isAvailableCoin: boolean = useMemo(getIsAvailableCoin, [
    game,
    wallet?.currentAccount?.coin?.id,
    getIsAvailableCoin,
  ]);

  const isLoading: boolean =
    isLoadingGame ||
    isLoadingDemoSlotSession ||
    isLoadingSlotSession ||
    isLoadingWallet;
  const isError: boolean =
    isErrorDemoSlotSession ||
    isErrorSlotSession ||
    !!errorGame ||
    !!errorWallet;
  const error: AxiosError<GenericError> =
    errorSlotSession || errorDemoSlotSession || errorGame || errorWallet;
  const queryError: AxiosError<GenericError> = errorGame || errorWallet;

  function handleQueryError() {
    if (errorWallet) {
      refetchWallet();
    }

    if (errorGame) {
      refetchGame();
    }
  }

  const handleRemoveSessionOnGameChange = useCallback(() => {
    if (currentPathname !== pathname && hasSlotSession) {
      store.dispatch(setHasSlotSession(false));
      toggleModeSelection();
      resetSlotSession();
      resetDemoSlotSession();
      setCurrentPathname(pathname);
    }
  }, [
    currentPathname,
    pathname,
    hasSlotSession,
    toggleModeSelection,
    resetSlotSession,
    resetDemoSlotSession,
  ]);

  useEffect(handleRemoveSessionOnGameChange, [handleRemoveSessionOnGameChange]);

  //eslint-disable-next-line
  const debouncedCreateSlotSession = useCallback(
    debounce(
      (data: CreateBettingSessionInputDto) => createBettingSession(data),
      500,
    ),
    [createBettingSession],
  );

  const handleCreateSlotSession = useCallback(() => {
    if (!AuthService.isAuthorized()) {
      navigateToModal(ModalsKey.Login);
      return;
    }

    if (!wallet?.currentAccount?.coin?.id) {
      return;
    }

    const isAvailableCoin = getIsAvailableCoin();
    const isAvailableLang = getIsAvailableLang();

    if (!isAvailableCoin || !isAvailableLang) {
      return;
    }

    debouncedCreateSlotSession({
      gameId: parseToBigInt(game?.id) as bigint,
      coinId: parseToBigInt(wallet?.currentAccount?.coin?.id) as bigint,
      lang: lang?.slice(0, 2),
      pairId: playCoin?.id,
      aggregatorId: GamingAggregatorId.Softswiss,
    });
  }, [
    game?.id,
    wallet?.currentAccount?.coin?.id,
    lang,
    playCoin,
    debouncedCreateSlotSession,
    getIsAvailableCoin,
    getIsAvailableLang,
    navigateToModal,
  ]);

  //eslint-disable-next-line
  const debouncedCreateDemoSlotSession = useCallback(
    debounce((data: CreateBettingSessionDemoInputDto) => {
      createBettingSessionDemo(data);
    }, 500),
    [createBettingSessionDemo],
  );

  const handleCreateDemoSlotSession = useCallback(() => {
    if (!game?.demo) {
      return;
    }

    const isAvailableLang = getIsAvailableLang();

    if (!isAvailableLang) {
      return;
    }

    debouncedCreateDemoSlotSession({
      gameId: parseToBigInt(game?.id) as bigint,
      lang: lang?.slice(0, 2),
      aggregatorId: GamingAggregatorId.Softswiss,
    });
  }, [
    game?.id,
    game?.demo,
    playCoin,
    lang,
    debouncedCreateDemoSlotSession,
    getIsAvailableLang,
  ]);

  const handleResetSlotSession = useCallback(() => {
    resetSlotSession();
    resetDemoSlotSession();
  }, [resetDemoSlotSession, resetSlotSession]);

  function initSlotGame() {
    if (typeof window == 'undefined') {
      return;
    }

    if (
      !game ||
      (isMobile && !mode) ||
      hasSlotSession ||
      !enabled ||
      showModeSelection
    ) {
      return;
    }

    handleResetSlotSession();

    if (selectedMode == CasinoGameMode.Demo) {
      handleCreateDemoSlotSession();
      return;
    }

    handleCreateSlotSession();
  }

  useEffect(initSlotGame, [
    mode,
    game,
    enabled,
    hasSlotSession,
    isMobile,
    selectedMode,
    showModeSelection,
    handleResetSlotSession,
    handleCreateSlotSession,
    handleCreateDemoSlotSession,
  ]);

  const availableCoins = useMemo(
    () => game?.provider?.coins?.map(data => data.coin),
    [game?.provider?.coins],
  );

  const availableLanguages = useMemo(
    () =>
      game?.provider?.languages
        ?.map(data => data.language)
        ?.filter(language => !language?.code?.includes('it')),
    [game?.provider?.languages],
  );

  function handleMount() {
    if (enabled) {
      store.dispatch(setHasSlotSession(false));
      setCookie(CookiesKey.CasinoLang, defaultLanguage, {
        sameSite: 'strict',
      });
      setLang(defaultLanguage);
    }
  }

  useEffect(handleMount, [enabled, defaultLanguage]);

  function onLanguageChange(lang: Language) {
    setLang(lang?.code);
    setCookie(CookiesKey.CasinoLang, lang?.code, {
      sameSite: 'strict',
    });
  }

  function onCoinChange(coin: Coin) {
    dispatch(setCasinoPlayCoin(coin));
  }

  return {
    isAvailableCoin,
    isAvailableLang,
    isLoading,
    hasDemo: game?.demo ?? false,
    handleError: queryError ? handleQueryError : initSlotGame,
    isBalanceError:
      (
        errorSlotSession as AxiosError<GenericError>
      )?.response?.data?.message?.includes('balance') ?? false,
    isError,
    error,
    game,
    slotSessionData:
      selectedMode == CasinoGameMode.Demo
        ? dataDemoSlotSession
        : dataSlotSession,
    showModeSelection,
    toggleModeSelection,
    wallet,
    availableCoins,
    availableLanguages,
    onLanguageChange,
    playCoin,
    gameStatus,
    onCoinChange,
  };
}
